import React, { useRef, useEffect, useState } from 'react';
import { CarfixEvent } from './CarHistoryTimeline.types';
import { SVGIcon } from '../SVGIcon';
import { APP_COLORS } from '@/config/colors';

interface TimelineItemCarfixProps {
  event: CarfixEvent;
}

/**
 * Element timeline napraw (Carfix).
 * Renderowanie dostosowuje się automatycznie na podstawie pól w event:
 * - dateUnknown → jaśniejszy pill "Data: nieznana"
 * - skpNumber / mileage → badge przebiegu + numer SKP
 * - description → szczegółowy opis naprawy zamiast krótkiego tytułu
 */
export const TimelineItemCarfix: React.FC<TimelineItemCarfixProps> = ({
  event,
}) => {
  const isDateUnknown = event.dateUnknown || !event.date;
  const isPlaceUnknown = event.placeUnknown || !event.place;

  const placeLabel = event.skpNumber
    ? `Miejsce: Numer SKP: ${event.skpNumber}`
    : isPlaceUnknown
      ? 'Miejsce: Nieznane'
      : `Miejsce: ${event.place}`;

  const formattedMileage = event.mileage
    ? event.mileage.toLocaleString('pl-PL')
    : null;

  // Refs do dynamicznego pomiaru odległości (jak w TimelineItemHSP)
  const dateRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [lineWidth, setLineWidth] = useState<number>(24);

  useEffect(() => {
    const calculateDistance = () => {
      if (!dateRef.current || !contentRef.current) return;
      const dateRect = dateRef.current.getBoundingClientRect();
      const contentRect = contentRef.current.getBoundingClientRect();
      const distance = contentRect.left - dateRect.right;
      setLineWidth(Math.max(0, distance));
    };

    calculateDistance();

    const resizeObserver = new ResizeObserver(() => calculateDistance());
    if (dateRef.current) resizeObserver.observe(dateRef.current);
    if (contentRef.current) resizeObserver.observe(contentRef.current);
    window.addEventListener('resize', calculateDistance);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', calculateDistance);
    };
  }, [event]);

  return (
    <div className="flex relative z-10 flex-col items-start mb-8 md:flex-row md:items-center">
      {/* Date pill */}
      <div className="flex relative flex-col mb-4 w-auto md:mb-0 md:w-48 md:items-center">
        <div
          ref={dateRef}
          className={`relative z-10 inline-block px-4 py-1.5 text-sm font-semibold rounded-button bg-primary-50 ${isDateUnknown ? 'text-primary-400' : 'text-primary uppercase'
            }`}
        >
          {isDateUnknown ? 'Data: nieznana' : event.date}

          {/* Horizontal connector line — dynamically measured, hidden on mobile */}
          <div
            className="hidden absolute top-1/2 left-full z-0 h-px -translate-y-1/2 md:block"
            style={{
              width: `${lineWidth}px`,
              backgroundColor: APP_COLORS.grayBorder,
            }}
          />
        </div>
      </div>

      {/* Card */}
      <div
        ref={contentRef}
        className="relative w-full bg-white border border-grayBorder rounded-card md:flex-1"
      >
        {/* Pin icon overlapping left edge — visible on all breakpoints like HSP */}
        <div className="absolute left-0 top-[28px] md:top-1/2 z-10 flex h-9 w-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-[10px] bg-blueLightest p-1">
          <SVGIcon
            name="map-marker"
            size="md"
            fill={APP_COLORS.blue.DEFAULT}
          />
        </div>

        {/* Card content: stacked on mobile, row on desktop */}
        <div className="flex flex-col md:flex-row md:items-center">
          {/* Left: place + optional mileage */}
          <div className="flex flex-col justify-center gap-1.5 p-4 pl-8 md:p-5 md:pl-[38px] md:w-[280px] md:shrink-0">
            <span className="text-sm text-grayText">{placeLabel}</span>
            {formattedMileage && (
              <span className="inline-block self-start px-3.5 py-1 text-xs font-medium tracking-wide rounded-button bg-grayText text-grayBorder">
                Przebieg: {formattedMileage} km
              </span>
            )}
          </div>

          {/* Divider: horizontal on mobile, vertical on desktop */}
          <div
            className="h-px md:mx-0 md:h-auto md:shrink-0 md:self-stretch md:w-px"
            style={{ backgroundColor: APP_COLORS.grayBorder }}
          />

          {/* Right: title or description */}
          <div className="flex-1 p-4 pt-2 md:px-7 md:py-3">
            <p className="text-base font-semibold text-text-primary font-archivo leading-snug">
              {event.description || event.title}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
